import { useDisclosure } from '@chakra-ui/react';
import {
  IBillGroupResp,
  ProductTypeEnum,
  SubscriptionBillingStatusEnum,
} from '@monetize/types/app';
import { DEFAULT_ROWS, TDataTablePager } from '@monetize/types/common';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetAccountById } from '../../../api/accountsService';
import { useGetRateById } from '../../../api/productCatalogService';
import { useGetById } from '../../../api/queryUtils';
import {
  useGetSubscriptionARR,
  useGetSubscriptionById,
  useGetSubscriptionEstimate,
  useGetSubscriptionHistory,
  useGetSubscriptionMRR,
  useGetSubscriptionTotal,
  useSubscriptionScheduledChanges,
} from '../../../api/subscriptionsService';
import {
  MBox,
  MButton,
  MCenter,
  MDivider,
  MFlex,
  MGrid,
  MPageContainer,
  MPageHeader,
  MPageLoader,
  MText,
} from '../../../components/Monetize';
import { ROUTES } from '../../../constants';
import {
  RATE_BILLING_FREQUENCY_MAP,
  getRateUsageBillingFrequencyDisplay,
} from '../../../constants/offerings';
import { SubscriptionBillingStatusDisplayText } from '../../../constants/subscriptions';
import { ACLCheck } from '../../../services/acl/ACLCheck';
import { sortByAlphabetically } from '../../../utils';
import { SubscriptionHeaderTag } from './components/SubscriptionHeaderTag';
import { CancelSubscription } from './components/SubscriptionOverview/CancelSubscription';
import { ScheduledChanges } from './components/SubscriptionOverview/ScheduledChanges';
import { SubscriptionDetails } from './components/SubscriptionOverview/SubscriptionDetails';
import { SubscriptionHistory } from './components/SubscriptionOverview/SubscriptionHistory';
import { TotalCard } from './components/SubscriptionOverview/TotalCard';
import { UsageDetails } from './components/SubscriptionOverview/UsageDetails';

export const SubscriptionOverview = () => {
  const navigate = useNavigate();
  const params = useParams();

  const subscriptionId = params.subscriptionId || '';
  const accountId = params.accountId || '';

  const { data: accountDetails, isLoading: accountDetailsLoading } =
    useGetAccountById(accountId, {
      enabled: !!accountId,
    });
  const { data: subscription, isLoading: subscriptionLoading } =
    useGetSubscriptionById(subscriptionId, {
      enabled: !!subscriptionId,
    });

  const { data: billGroup, isLoading: billGroupLoading } =
    useGetById<IBillGroupResp>('billGroups', subscription?.billGroupId!, {
      enabled: !!subscription?.billGroupId,
    });

  const {
    open: isCancelModalOpen,
    onOpen: onOpenCancelModal,
    onClose: onCloseCancelModal,
  } = useDisclosure();

  const [pager] = useState<TDataTablePager>({
    first: 0,
    rows: DEFAULT_ROWS,
    page: 0,
  });

  const { data: subscriptionHistory, isLoading: subscriptionHistoryLoading } =
    useGetSubscriptionHistory(pager, accountId, subscriptionId, {
      enabled: !!accountId && !!subscription,
    });

  const { data: mrrAmount, isLoading: mrrAmountLoading } =
    useGetSubscriptionMRR(accountId, subscriptionId, {
      enabled: !!accountId && !!subscription,
    });

  const { data: arrAmount, isLoading: arrAmountLoading } =
    useGetSubscriptionARR(accountId, subscriptionId, {
      enabled: !!accountId && !!subscription,
    });

  const { data: estimateAmount, isLoading: estimateLoading } =
    useGetSubscriptionEstimate(accountId, subscriptionId, {
      enabled: !!accountId && !!subscription,
    });

  const { data: totalAmount, isLoading: totalAmountLoading } =
    useGetSubscriptionTotal(accountId, subscriptionId, {
      enabled: !!accountId && !!subscription,
    });

  const { data: rate, isLoading: rateLoading } = useGetRateById(
    subscription?.rateId!,
    {
      enabled: !!subscription?.rateId,
    },
  );

  const { data: scheduledChanges, isLoading: scheduleChangesLoading } =
    useSubscriptionScheduledChanges(subscriptionId);

  const isCancelled =
    subscription?.billingStatus === SubscriptionBillingStatusEnum.CANCELED;

  // scheduled changes API not implemented yet
  const hasScheduledChanges = !!scheduledChanges && scheduledChanges.length > 0;

  const productSubscriptionItems =
    subscription?.subscriptionItems.filter(
      (item) => item.product?.productType !== ProductTypeEnum.USAGE,
    ) || [];

  const usageSubscriptionItems =
    subscription?.subscriptionItems
      .filter((item) => item.product?.productType === ProductTypeEnum.USAGE)
      .sort((a, b) => sortByAlphabetically()(a.product, b.product)) || [];

  const isLoading =
    accountDetailsLoading ||
    subscriptionLoading ||
    subscriptionHistoryLoading ||
    mrrAmountLoading ||
    arrAmountLoading ||
    estimateLoading ||
    totalAmountLoading ||
    rateLoading ||
    scheduleChangesLoading ||
    billGroupLoading ||
    !billGroup;

  if (isLoading) {
    return <MPageLoader />;
  }

  return (
    <MPageContainer alignItems="stretch" enableAccountSearch>
      <MPageHeader
        hasBackButton
        parentLink={ROUTES.getAccountSubscriptionsRoute(accountId)}
        parentLinkTitle={accountDetails?.accountName}
        title={`${subscription?.offering?.name || ''} - ${rate?.name}`}
        status={
          subscription?.billingStatus
            ? SubscriptionBillingStatusDisplayText[subscription?.billingStatus]
            : ''
        }
        tag={
          <MBox display="flex">
            {SubscriptionHeaderTag(
              subscription?.locked || false,
              hasScheduledChanges,
            )}
          </MBox>
        }
        id={subscription?.id}
        copyUrl
        subTitle={
          <MFlex>
            {rate?.billingFrequency && (
              <>
                <MText fontWeight="normal">{`Billing Frequency:  ${
                  RATE_BILLING_FREQUENCY_MAP[rate.billingFrequency](
                    rate.billingFrequencyInMonths,
                  ).label
                }`}</MText>
                <MCenter w={4} height={4} mt={0.5}>
                  <MDivider
                    orientation="vertical"
                    borderColor="tGray.darkGrayPurple"
                  />
                </MCenter>
              </>
            )}
            {rate && (
              <MText fontWeight="normal">{`Usage Frequency:  ${getRateUsageBillingFrequencyDisplay(
                rate.usageBillingFrequency,
              )}`}</MText>
            )}
          </MFlex>
        }
        recordAuditInfo={subscription}
        entityJson={subscription}
      >
        <MFlex align="center">
          <ACLCheck acls={[['billing', 'update']]}>
            {subscription && !isCancelled && !subscription?.locked && (
              <MButton
                data-testid="cancel-subscription"
                variant="deleteOutline"
                isDisabled={!subscription}
                onClick={onOpenCancelModal}
              >
                Cancel Subscription
              </MButton>
            )}
          </ACLCheck>
        </MFlex>
      </MPageHeader>
      <MBox flex="1" mt="4" px={8}>
        <MGrid
          justifyItems="center"
          alignItems="center"
          gridTemplateColumns="repeat(4, 1fr 0.1fr)"
          mt={2}
        >
          <TotalCard
            labelName="MRR"
            totalAmount={mrrAmount?.total}
            currency={billGroup?.currency}
          />
          <MDivider orientation="vertical" />
          <TotalCard
            labelName="ARR"
            totalAmount={arrAmount?.total}
            currency={billGroup?.currency}
          />
          <MDivider orientation="vertical" />
          <TotalCard
            labelName="Renewal Estimate"
            totalAmount={estimateAmount?.total}
            currency={billGroup?.currency}
          />
          <MDivider orientation="vertical" />
          <TotalCard
            labelName="Total Spend"
            totalAmount={totalAmount?.total}
            currency={billGroup?.currency}
          />
        </MGrid>

        <MDivider mt={6} mb={2} />

        {subscription && productSubscriptionItems.length > 0 && (
          <SubscriptionDetails
            productSubscriptionItems={productSubscriptionItems}
            rate={rate}
            subscription={subscription}
          />
        )}

        {subscription && usageSubscriptionItems.length > 0 && (
          <UsageDetails
            subscription={subscription}
            usageSubscriptionItems={usageSubscriptionItems}
            rate={rate}
            accountId={accountId}
          />
        )}

        <MBox h="3" />
        {hasScheduledChanges && (
          <ScheduledChanges
            scheduledChanges={scheduledChanges}
            subscriptionItems={subscription?.subscriptionItems}
          />
        )}

        <SubscriptionHistory
          subscriptionHistory={subscriptionHistory?.content || []}
        />
      </MBox>

      <CancelSubscription
        isOpen={isCancelModalOpen}
        onClose={(redirect: boolean = true) => {
          onCloseCancelModal();
          redirect && navigate(ROUTES.getAccountSubscriptionsRoute(accountId));
        }}
        accountId={accountId}
        billGroupId={subscription?.billGroupId || ''}
        subscriptionId={subscriptionId}
      />
    </MPageContainer>
  );
};
